import { fetchDistrictCatalog } from '@/api/districtCatalog';
import {
  getAgencySelectedDistricts,
  updateAgencyLeadRegion,
  type AgencySelectedDistrict,
} from '@/api/sellerLeads';
import {
  CatalogLoadStatus,
  type DistrictOption,
  type SelectedDistrictOption,
} from '@/types-district-catalog';
import type { ActionContext } from 'vuex';

export interface DistrictCatalogState {
  catalog: DistrictOption[];
  selectedDistrictIds: string[];
  savedDistrictIds: string[];
  savedDistrictSelections: AgencySelectedDistrict[];
  /**
   * District IDs whose persisted partial-coverage selection should be ignored
   * for the current in-memory selection. Populated when the user removes a
   * district that had a saved partial selection and re-adds it in the same
   * session: the re-added chip must default to full coverage instead of
   * inheriting the stale partial postcode subset. Cleared on save and on
   * reset/replace of the selection.
   */
  discardedSavedSelectionDistrictIds: string[];
  catalogLoadStatus: CatalogLoadStatus;
  savedDistrictsLoadStatus: CatalogLoadStatus;
}

export const getInitialState = (): DistrictCatalogState => ({
  catalog: [],
  selectedDistrictIds: [],
  savedDistrictIds: [],
  savedDistrictSelections: [],
  discardedSavedSelectionDistrictIds: [],
  catalogLoadStatus: CatalogLoadStatus.IDLE,
  savedDistrictsLoadStatus: CatalogLoadStatus.IDLE,
});

const toSelectedDistrictOption = (
  district: DistrictOption,
  savedSelection?: AgencySelectedDistrict,
): SelectedDistrictOption => {
  const selectedPostalCodeIds =
    savedSelection?.postalCodeIds ?? district.entries.map((entry) => entry.id);
  const selectedPostalCodes = [
    ...new Set(
      district.entries
        .filter((entry) => selectedPostalCodeIds.includes(entry.id))
        .map((entry) => entry.postalCode),
    ),
  ];
  // Salesforce matches at postcode granularity, so partial coverage is defined
  // on the set of postcodes — not per-(postcode, city) catalog entries.
  // `district.postcodes` is the canonical unique postcode set built by
  // `buildDistrictCatalog`; reusing it keeps the source of truth in one place.
  const isPartiallyCovered =
    district.postcodes.length > 0 &&
    selectedPostalCodes.length > 0 &&
    selectedPostalCodes.length < district.postcodes.length;

  return {
    ...district,
    isPartiallyCovered,
    selectedPostalCodeIds,
    selectedPostalCodes,
  };
};

const getDistrictSelectedPostalCodeIds = (
  district?: DistrictOption,
  savedSelection?: AgencySelectedDistrict,
): string[] => {
  if (!district) {
    return [];
  }

  if (!savedSelection) {
    return district.entries.map((entry) => entry.id);
  }

  const districtEntryIds = district.entries.map((entry) => entry.id);

  return savedSelection.postalCodeIds.filter((postalCodeId) =>
    districtEntryIds.includes(postalCodeId),
  );
};

const compareByLabel = <T extends { label: string }>(a: T, b: T): number =>
  a.label.localeCompare(b.label, 'de-CH');

const compareLabels = (a: string, b: string): number => a.localeCompare(b, 'de-CH');

const getters = {
  getCatalog: (state: DistrictCatalogState): DistrictOption[] =>
    [...state.catalog].sort(compareByLabel),

  getSelectedDistricts: (state: DistrictCatalogState): SelectedDistrictOption[] =>
    state.selectedDistrictIds
      .map((id) => {
        const district = state.catalog.find((d) => d.id === id);
        if (!district) return undefined;

        const savedSelection = state.discardedSavedSelectionDistrictIds.includes(id)
          ? undefined
          : state.savedDistrictSelections.find((selection) => selection.district === id);

        return toSelectedDistrictOption(district, savedSelection);
      })
      .filter((d): d is SelectedDistrictOption => d !== undefined)
      .sort(compareByLabel),

  getSelectedDistrictLabels: (state: DistrictCatalogState): string[] =>
    state.selectedDistrictIds
      .map((id) => state.catalog.find((d) => d.id === id)?.label)
      .filter((label): label is string => label !== undefined)
      .sort(compareLabels),

  getSavedDistricts: (state: DistrictCatalogState): SelectedDistrictOption[] =>
    state.savedDistrictIds
      .map((id) => {
        const district = state.catalog.find((d) => d.id === id);
        if (!district) return undefined;

        return toSelectedDistrictOption(
          district,
          state.savedDistrictSelections.find((selection) => selection.district === id),
        );
      })
      .filter((d): d is SelectedDistrictOption => d !== undefined)
      .sort(compareByLabel),

  getSavedDistrictLabels: (state: DistrictCatalogState): string[] =>
    state.savedDistrictIds
      .map((id) => state.catalog.find((d) => d.id === id)?.label)
      .filter((label): label is string => label !== undefined)
      .sort(compareLabels),

  getCatalogLoadStatus: (state: DistrictCatalogState): CatalogLoadStatus => state.catalogLoadStatus,

  getSavedDistrictsLoadStatus: (state: DistrictCatalogState): CatalogLoadStatus =>
    state.savedDistrictsLoadStatus,

  hasNoCatalog: (state: DistrictCatalogState): boolean =>
    state.catalog.length === 0 && state.catalogLoadStatus === CatalogLoadStatus.SUCCESS,

  getDistrictPostcodesMap: (state: DistrictCatalogState): Map<string, string[]> =>
    new Map(state.catalog.map((d) => [d.id, d.postcodes])),

  getSelectedDistrictIds: (state: DistrictCatalogState): string[] => state.selectedDistrictIds,

  getSavedDistrictIds: (state: DistrictCatalogState): string[] => state.savedDistrictIds,

  /**
   * Districts that are present in both the in-memory selection and the saved
   * selection but whose persisted partial coverage will be promoted to full
   * coverage on save. The returned `postcodes` are the postcodes that will be
   * **newly added** to that district's coverage (full set minus the
   * previously-saved subset). Used by the save-summary modal so the user sees
   * exactly which postcodes a partial→full re-add will add to their region.
   */
  getDistrictsWithExpandedCoverage: (
    state: DistrictCatalogState,
  ): Array<{ id: string; label: string; postcodes: string[] }> =>
    state.selectedDistrictIds
      .filter(
        (districtId) =>
          state.savedDistrictIds.includes(districtId) &&
          state.discardedSavedSelectionDistrictIds.includes(districtId),
      )
      .map((districtId) => {
        const district = state.catalog.find((d) => d.id === districtId);
        if (!district) return undefined;

        const savedSelection = state.savedDistrictSelections.find(
          (selection) => selection.district === districtId,
        );
        const previouslyCovered = new Set(
          district.entries
            .filter((entry) => savedSelection?.postalCodeIds.includes(entry.id))
            .map((entry) => entry.postalCode),
        );
        const newlyCoveredPostcodes = district.postcodes.filter(
          (postcode) => !previouslyCovered.has(postcode),
        );

        if (newlyCoveredPostcodes.length === 0) {
          return undefined;
        }

        return {
          id: district.id,
          label: district.label,
          postcodes: newlyCoveredPostcodes,
        };
      })
      .filter(
        (entry): entry is { id: string; label: string; postcodes: string[] } => entry !== undefined,
      )
      .sort(compareByLabel),

  hasSelectionChanges: (state: DistrictCatalogState): boolean => {
    const idsDiffer =
      state.selectedDistrictIds.length !== state.savedDistrictIds.length ||
      state.selectedDistrictIds.some((districtId) => !state.savedDistrictIds.includes(districtId));
    if (idsDiffer) return true;

    return state.selectedDistrictIds.some((districtId) =>
      state.discardedSavedSelectionDistrictIds.includes(districtId),
    );
  },
};

const mutations = {
  setCatalog(state: DistrictCatalogState, catalog: DistrictOption[]): void {
    state.catalog = catalog;
  },

  setCatalogLoadStatus(state: DistrictCatalogState, status: CatalogLoadStatus): void {
    state.catalogLoadStatus = status;
  },

  addDistrict(state: DistrictCatalogState, districtId: string): void {
    const wasAlreadySelected = state.selectedDistrictIds.includes(districtId);
    if (!wasAlreadySelected) {
      state.selectedDistrictIds.push(districtId);
    }
    const hasSavedSelection = state.savedDistrictSelections.some(
      (selection) => selection.district === districtId,
    );
    if (
      !wasAlreadySelected &&
      hasSavedSelection &&
      !state.discardedSavedSelectionDistrictIds.includes(districtId)
    ) {
      state.discardedSavedSelectionDistrictIds.push(districtId);
    }
  },

  removeDistrict(state: DistrictCatalogState, districtId: string): void {
    state.selectedDistrictIds = state.selectedDistrictIds.filter((id) => id !== districtId);
  },

  setSelectedDistrictIds(state: DistrictCatalogState, ids: string[]): void {
    state.selectedDistrictIds = [...ids];
    state.discardedSavedSelectionDistrictIds = [];
  },

  setSavedDistrictIds(state: DistrictCatalogState, ids: string[]): void {
    state.savedDistrictIds = [...ids];
  },

  setSavedDistrictSelections(
    state: DistrictCatalogState,
    selections: AgencySelectedDistrict[],
  ): void {
    state.savedDistrictSelections = selections.map((selection) => ({
      district: selection.district,
      postalCodeIds: [...selection.postalCodeIds],
    }));
    state.discardedSavedSelectionDistrictIds = [];
  },

  setSavedDistrictsLoadStatus(state: DistrictCatalogState, status: CatalogLoadStatus): void {
    state.savedDistrictsLoadStatus = status;
  },

  resetSavedDistricts(state: DistrictCatalogState): void {
    state.selectedDistrictIds = [];
    state.savedDistrictIds = [];
    state.savedDistrictSelections = [];
    state.discardedSavedSelectionDistrictIds = [];
    state.savedDistrictsLoadStatus = CatalogLoadStatus.IDLE;
  },
};

const actions = {
  async fetchCatalog({
    commit,
    state,
  }: ActionContext<DistrictCatalogState, unknown>): Promise<void> {
    if (
      state.catalogLoadStatus === CatalogLoadStatus.SUCCESS ||
      state.catalogLoadStatus === CatalogLoadStatus.LOADING
    )
      return;

    commit('setCatalogLoadStatus', CatalogLoadStatus.LOADING);
    try {
      const catalog = await fetchDistrictCatalog();
      commit('setCatalog', catalog);
      commit('setCatalogLoadStatus', CatalogLoadStatus.SUCCESS);
    } catch (error) {
      console.error('Failed to fetch district catalog:', error);
      commit('setCatalogLoadStatus', CatalogLoadStatus.ERROR);
    }
  },

  initializeSelection(
    { commit }: ActionContext<DistrictCatalogState, unknown>,
    districtIds: string[],
  ): void {
    commit('setSelectedDistrictIds', districtIds);
  },

  resetSelectionToSaved({ commit, state }: ActionContext<DistrictCatalogState, unknown>): void {
    commit('setSelectedDistrictIds', state.savedDistrictIds);
  },

  resetSavedDistricts({ commit }: ActionContext<DistrictCatalogState, unknown>): void {
    commit('resetSavedDistricts');
  },

  async fetchSavedDistricts(
    { commit, state }: ActionContext<DistrictCatalogState, unknown>,
    options?: { force?: boolean },
  ): Promise<void> {
    const force = options?.force ?? false;
    if (
      (!force && state.savedDistrictsLoadStatus === CatalogLoadStatus.SUCCESS) ||
      state.savedDistrictsLoadStatus === CatalogLoadStatus.LOADING
    ) {
      return;
    }

    commit('setSavedDistrictsLoadStatus', CatalogLoadStatus.LOADING);
    try {
      const rows = await getAgencySelectedDistricts();
      if (rows === null) {
        commit('setSavedDistrictsLoadStatus', CatalogLoadStatus.ERROR);
        return;
      }
      commit('setSavedDistrictSelections', rows);
      const districtIds = rows.map((row) => row.district);
      commit('setSavedDistrictIds', districtIds);
      if (force || state.selectedDistrictIds.length === 0) {
        commit('setSelectedDistrictIds', districtIds);
      }
      commit('setSavedDistrictsLoadStatus', CatalogLoadStatus.SUCCESS);
    } catch (error) {
      console.error('Failed to fetch saved districts:', error);
      commit('setSavedDistrictsLoadStatus', CatalogLoadStatus.ERROR);
    }
  },

  async saveSelectedDistricts({
    state,
    commit,
  }: ActionContext<DistrictCatalogState, unknown>): Promise<boolean> {
    const resolveSavedSelection = (districtId: string): AgencySelectedDistrict | undefined => {
      if (state.discardedSavedSelectionDistrictIds.includes(districtId)) {
        return undefined;
      }
      return state.savedDistrictSelections.find((selection) => selection.district === districtId);
    };

    const postalCodeIds = state.catalog
      .filter((district) => state.selectedDistrictIds.includes(district.id))
      .flatMap((district) =>
        getDistrictSelectedPostalCodeIds(district, resolveSavedSelection(district.id)),
      );

    const uniquePostalCodeIds = [...new Set(postalCodeIds)];
    const result = await updateAgencyLeadRegion(uniquePostalCodeIds);

    if (!result) {
      return false;
    }

    commit('setSavedDistrictIds', state.selectedDistrictIds);
    commit(
      'setSavedDistrictSelections',
      state.selectedDistrictIds.map((districtId) => ({
        district: districtId,
        postalCodeIds: getDistrictSelectedPostalCodeIds(
          state.catalog.find((district) => district.id === districtId),
          resolveSavedSelection(districtId),
        ),
      })),
    );
    return true;
  },
};

const _default = {
  namespaced: true,
  state: getInitialState,
  getters,
  mutations,
  actions,
};

export default _default;
